// ---------------------------------------------------------------------------
// The 6 goodness-of-fit indicators CalEvBasic uses to score a calibration
// candidate (AlgoritmoCalculo.vb, `F1Nash`..`F6RSR`), ported formula for
// formula — see objectiveFunction.ts for how they combine into a single
// value to maximize (CalEvBasic's own "maximizar o minimizar" logic, the
// reason this whole feature is based on that project).
//
// `window` is inclusive on both ends (VB's `For i = ini To fin`), 0-based
// into `observed`/`simulated` — the same range CalEvBasic calls
// PosInicioCalib/PosFinalCalib. Gaps in the observed series (VB's `-100`
// sentinel) are `NaN` here, matching this app's existing gap convention
// elsewhere (dailySeries.ts). `simulated` comes straight from this app's own
// engine, so it's never expected to have gaps — only `observed` is checked.
//
// A window with zero valid observed points, or a completely flat observed
// series (denominator sums of zero), divides by zero exactly as the VB does
// — silently producing NaN/Infinity, not guarded here on purpose (an empty
// calibration window is a Phase 3 UI-entry validation concern: refuse to
// start a calibration whose window has no usable observed data, don't
// silently patch it here — see project_evalhid_calibracion.md).
// ---------------------------------------------------------------------------

export interface IndicatorWindow {
  /** Inclusive. */
  start: number;
  /** Inclusive. */
  end: number;
}

function meanValid(values: number[], window: IndicatorWindow): number {
  let sum = 0;
  let gaps = 0;
  for (let i = window.start; i <= window.end; i++) {
    if (Number.isNaN(values[i])) gaps++;
    else sum += values[i];
  }
  return sum / (window.end - window.start + 1 - gaps);
}

/** F1Nash — Nash-Sutcliffe efficiency (NSE). 1 is a perfect match, higher is better. */
export function nash(observed: number[], simulated: number[], window: IndicatorWindow): number {
  const meanObserved = meanValid(observed, window);
  let sumSquaredError = 0;
  let sumSquaredDeviation = 0;
  for (let i = window.start; i <= window.end; i++) {
    if (Number.isNaN(observed[i])) continue;
    sumSquaredError += (simulated[i] - observed[i]) ** 2;
    sumSquaredDeviation += (observed[i] - meanObserved) ** 2;
  }
  return 1 - sumSquaredError / sumSquaredDeviation;
}

/**
 * F2LogNash — Nash-Sutcliffe on the log of both series (weights low flows
 * more than F1). `cte` avoids log(0), same value as the source.
 *
 * Faithful to a real quirk in the original: the "mean" used inside
 * `Math.log(...)` for the denominator is the mean of the RAW observed
 * values, not the mean of their logs (`promedioLogObservados` in
 * AlgoritmoCalculo.vb accumulates `observados(i)`, never
 * `Math.Log(observados(i))`) — replicated as-is, not "fixed", same policy
 * this app already applies to the main calculation engine (see CLAUDE.md).
 */
export function logNash(observed: number[], simulated: number[], window: IndicatorWindow): number {
  const cte = 0.0001;
  const meanRawObservedUsedAsIfLog = meanValid(observed, window);
  let sumSquaredError = 0;
  let sumSquaredDeviation = 0;
  for (let i = window.start; i <= window.end; i++) {
    if (Number.isNaN(observed[i])) continue;
    sumSquaredError += (Math.log(simulated[i] + cte) - Math.log(observed[i] + cte)) ** 2;
    sumSquaredDeviation +=
      (Math.log(observed[i] + cte) - Math.log(meanRawObservedUsedAsIfLog)) ** 2;
  }
  return 1 - sumSquaredError / sumSquaredDeviation;
}

/** F3Pearson — linear correlation coefficient between observed and simulated, in [-1, 1]. */
export function pearson(observed: number[], simulated: number[], window: IndicatorWindow): number {
  const meanObserved = meanValid(observed, window);
  const meanSimulated = meanValid(simulated, window);
  let covariance = 0;
  let varianceSimulated = 0;
  let varianceObserved = 0;
  for (let i = window.start; i <= window.end; i++) {
    if (Number.isNaN(observed[i])) continue;
    covariance += (simulated[i] - meanSimulated) * (observed[i] - meanObserved);
    varianceSimulated += (simulated[i] - meanSimulated) ** 2;
    varianceObserved += (observed[i] - meanObserved) ** 2;
  }
  return covariance / Math.sqrt(varianceSimulated * varianceObserved);
}

/**
 * F4Simetria — how close the mean of simulated is to the mean of observed,
 * symmetric either way (over- or under-estimating the mean are penalized
 * the same). 1 is a perfect match.
 */
export function symmetry(observed: number[], simulated: number[], window: IndicatorWindow): number {
  const meanObserved = meanValid(observed, window);
  const meanSimulated = meanValid(simulated, window);
  const ratioA = meanSimulated / meanObserved;
  const ratioB = meanObserved / meanSimulated;
  const worstRatio = Math.max(ratioA, ratioB);
  return 1 - (worstRatio - 1) ** 2;
}

/**
 * F5Pbias — percent bias, as a fraction (not ×100, unlike most published
 * definitions — same as the source). 0 is unbiased; positive means the
 * model underestimates on average, negative means it overestimates.
 */
export function percentBias(
  observed: number[],
  simulated: number[],
  window: IndicatorWindow
): number {
  let sumDifference = 0;
  let sumObserved = 0;
  for (let i = window.start; i <= window.end; i++) {
    if (Number.isNaN(observed[i])) continue;
    sumDifference += observed[i] - simulated[i];
    sumObserved += observed[i];
  }
  return sumDifference / sumObserved;
}

/** F6RSR — RMSE-observations standard deviation ratio. 0 is a perfect match, lower is better. */
export function rsr(observed: number[], simulated: number[], window: IndicatorWindow): number {
  const meanObserved = meanValid(observed, window);
  let sumSquaredError = 0;
  let sumSquaredDeviation = 0;
  for (let i = window.start; i <= window.end; i++) {
    if (Number.isNaN(observed[i])) continue;
    sumSquaredError += (observed[i] - simulated[i]) ** 2;
    sumSquaredDeviation += (observed[i] - meanObserved) ** 2;
  }
  return Math.sqrt(sumSquaredError) / Math.sqrt(sumSquaredDeviation);
}
